import { Controller } from "@hotwired/stimulus"
import { patch, post } from "@rails/request.js"
import { confirmGoatProfilePhone } from "../lib/goat_profile_phone_confirm"
import { clearButtonBusy, markButtonBusy } from "../lib/button_loading"

// Buy-leads phone verification: the edit form, and the Send / Resend links.
// The server refuses a GOAT-owned number until acknowledge_goat_number is
// sent. Keep (and every dismiss) leaves the phone alone and sends no code.
// The control that started the exchange shows the BUTTON-8 busy state
// (spinner, label unchanged, aria-busy) until it settles; on success it stays
// busy while the browser follows the redirect.
export default class extends Controller {
  static targets = ["phoneInput", "ack", "message"]

  async guardSubmit(event) {
    event.preventDefault()
    if (this.pending) return
    if (!this.hasPhoneInputTarget) return

    const phone = this.phoneInputTarget.value.trim()
    if (!phone) {
      this.showError("Phone number cannot be blank.")
      return
    }

    const button = event.submitter || this.element.querySelector('[type="submit"]')
    await this.exchange(button, (acknowledge) => patch(this.element.action, {
      body: { phone, acknowledge_goat_number: acknowledge ? "1" : "0" },
      responseKind: "json",
    }))
  }

  async guardLink(event) {
    event.preventDefault()
    if (this.pending) return

    const url = event.currentTarget.getAttribute("href")
    await this.exchange(event.currentTarget, (acknowledge) => post(url, {
      body: { acknowledge_goat_number: acknowledge ? "1" : "0" },
      responseKind: "json",
    }))
  }

  async exchange(button, requestFor) {
    this.pending = true
    markButtonBusy(button)
    let acknowledge = false
    let leaving = false
    try {
      for (;;) {
        const response = await requestFor(acknowledge)
        const data = await response.json
        if (data.error_code === "goat_number" && !acknowledge) {
          const useAnyway = await confirmGoatProfilePhone()
          if (!useAnyway) return
          if (this.hasAckTarget) this.ackTarget.value = "1"
          acknowledge = true
          continue
        }
        if (data.success && data.redirect_url) {
          leaving = true
          window.location.assign(data.redirect_url)
          return
        }
        this.showError(data.message || data.error || "Could not send a verification code. Please try again.")
        return
      }
    } catch {
      this.showError("Something went wrong. Please try again.")
    } finally {
      this.pending = false
      if (!leaving) clearButtonBusy(button)
    }
  }

  showError(text) {
    if (!this.hasMessageTarget) return
    this.messageTarget.classList.remove("d-none")
    this.messageTarget.textContent = text
  }
}
